import React, { useState, useEffect, useRef } from "react";
import { X, Plus, Info } from "lucide-react";

const SECTOR_OPTIONS = [
  { value: "construction", label: "Construction / Built environment", coming: false },
  { value: "textiles", label: "Textiles", coming: true },
  { value: "electronics", label: "Electronics", coming: true },
  { value: "furniture", label: "Furniture", coming: true },
  { value: "packaging", label: "Packaging products", coming: true },
  { value: "consumer", label: "Consumer products", coming: false },
  { value: "generic", label: "Generic (Cross-sector)", coming: false },
];

const FU_UNITS = ["pcs", "m²", "m³", "m", "kg", "t", "kWh", "MJ", "l"];

const defaultParams = [
  { key: "type", label: "Type", values: [] },
  { key: "dimensions", label: "Dimensions", values: [] },
  { key: "treatment", label: "Treatment", values: [] },
  { key: "glazing", label: "Glazing", values: [] },
  { key: "material", label: "Material", values: [] },
];

const buildParamsFromSubgroup = (subgroups, categoryId) => {
  const sg = subgroups?.find((s) => s.id === categoryId);
  if (sg?.variation_categories && Array.isArray(sg.variation_categories) && sg.variation_categories.length > 0) {
    const byName = new Map();

    sg.variation_categories.forEach((vc) => {
      const name = vc?.name || "";
      if (!name) return;
      if (!byName.has(name)) {
        byName.set(name, new Set());
      }
      const set = byName.get(name);
      if (Array.isArray(vc.values)) {
        vc.values.forEach((v) => {
          const val = typeof v === "object" ? v.value : v;
          if (val) set.add(String(val));
        });
      }
    });

    const unique = [];
    byName.forEach((valuesSet, name) => {
      const options = Array.from(valuesSet);
      unique.push({
        key: name.toLowerCase().replace(/\s+/g, "-"),
        label: name,
        values: [],
        options,
      });
    });

    if (unique.length > 0) {
      return unique;
    }
  }
  // Fallback to default params when subgroup has no configured (unique) variations
  return defaultParams.map((p) => ({ ...p }));
};

function NewProductDialog({
  categories = [],
  defaultCategoryId = "",
  defaultBusiness,
  knownVariables = [],
  onAddKnownVariable = () => {},
  onConfirm,
  onClose,
  subgroups = [],
  year,
}) {
  const [name, setName] = useState("");
  const [productNumber, setProductNumber] = useState("");
  const [variant, setVariant] = useState("");
  const [variation, setVariation] = useState("");
  const [fuValue, setFuValue] = useState("1");
  const [fuUnit, setFuUnit] = useState("pcs");
  const initialCategoryId = defaultCategoryId || (subgroups?.[0]?.id ?? "");
  const [categoryId, setCategoryId] = useState(initialCategoryId);
  const [params, setParams] = useState(() => buildParamsFromSubgroup(subgroups, initialCategoryId));
  const [newParamLabel, setNewParamLabel] = useState("");
  const [sector, setSector] = useState("construction");
  const [draftValueByKey, setDraftValueByKey] = useState({});
  const modalRef = useRef(null);

  const categoriesList = categories.length > 0 ? categories : (subgroups || []).map((s) => ({ id: s.id, label: s.name }));

  useEffect(() => {
    if (!categoryId && categoriesList.length > 0) {
      setCategoryId(categoriesList[0].id);
    }
  }, [categoryId, categoriesList]);

  const handleCategoryChange = (id) => {
    setCategoryId(id);
    setVariation("");
  };

  // When the selected category (subgroup) changes, pull its variation types from backend
  useEffect(() => {
    if (!categoryId) return;
    setParams(buildParamsFromSubgroup(subgroups, categoryId));
  }, [categoryId, subgroups]);

  const selectedSectorOption = SECTOR_OPTIONS.find((s) => s.value === sector);

  const updateParamValues = (key, values) =>
    setParams((prev) => prev.map((p) => (p.key === key ? { ...p, values } : p)));

  const toggleParamValue = (key, value) => {
    setParams((prev) =>
      prev.map((p) => {
        if (p.key !== key) return p;
        const arr = p.values || [];
        const has = arr.includes(value);
        return { ...p, values: has ? arr.filter((v) => v !== value) : [...arr, value] };
      })
    );
  };

  const addParamValue = (key, value) => {
    const trimmed = String(value).trim();
    if (!trimmed) return;
    setParams((prev) =>
      prev.map((p) => {
        if (p.key !== key) return p;
        const arr = p.values || [];
        if (arr.includes(trimmed)) return p;
        return { ...p, values: [...arr, trimmed] };
      })
    );
  };

  const removeParamValue = (key, valueToRemove) => {
    setParams((prev) =>
      prev.map((p) =>
        p.key === key
          ? { ...p, values: (p.values || []).filter((v) => v !== valueToRemove) }
          : p
      )
    );
  };

  const addParam = (label) => {
    if (!label || !String(label).trim()) return;
    const key = String(label).toLowerCase().replace(/\s+/g, "-");
    if (params.some((p) => p.key === key)) return;
    setParams((prev) => [...prev, { key, label: String(label).trim(), values: [], options: [] }]);
    onAddKnownVariable(String(label).trim());
    setNewParamLabel("");
  };

  const removeParam = (key) => setParams((prev) => prev.filter((p) => p.key !== key));

  const handleConfirm = () => {
    if (!name.trim()) return;
    const chosenSubgroup = subgroups?.find((s) => s.id === categoryId) || { id: categoryId, business: defaultBusiness ?? "" };
    const variationCategories = params.map((p) => ({
      name: p.label,
      values: (p.values || []).map((v) => ({ value: v })),
    }));
    onConfirm({
      name: name.trim(),
      productNum: productNumber,
      variant,
      variation,
      fuValue,
      fuUnit,
      categoryId,
      subgroupId: chosenSubgroup.id,
      business: chosenSubgroup.business,
      params,
      sector,
      variationCategories,
    });
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "var(--card-bg, #fff)",
          borderColor: "var(--border-color, #e5e7eb)",
        }}
      >
        <div
          className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 z-10"
          style={{ borderColor: "var(--border-color, #e5e7eb)", backgroundColor: "var(--card-bg, #fff)" }}
        >
          <h3 className="text-base font-semibold text-foreground" style={{ color: "var(--foreground, #111)" }}>
            New Product
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            style={{ color: "var(--muted-foreground, #6b7280)" }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-6" style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Sector */}
          <section style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider" style={{ color: "#6b7280", fontSize: "11px" }}>
              Sector *
            </h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <select
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                className="h-9 text-sm rounded-lg border border-border bg-background px-3 w-full"
                style={{ minHeight: "36px", borderColor: "#e5e7eb" }}
              >
                {SECTOR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                    {opt.coming ? " (Coming)" : ""}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground mt-1.5" style={{ color: "#6b7280", marginTop: "6px" }}>
                Sector-specific LCA templates will be enabled progressively.
              </p>
              {selectedSectorOption?.coming && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1" style={{ display: "flex", alignItems: "center", gap: "6px", color: "#6b7280" }}>
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  <span>Sector-specific LCA templates are not yet enabled. The product will use the Generic LCA template.</span>
                </div>
              )}
            </div>
          </section>

          {/* Basic info */}
          <section style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider" style={{ color: "#6b7280", fontSize: "11px" }}>
              Basic Information
            </h4>
            <div className="grid grid-cols-2 gap-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <label className="text-xs font-medium text-muted-foreground" style={{ color: "#6b7280" }}>Product Name *</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. VELUX GDL Cabrio®"
                  className="h-9 text-sm rounded-md border border-input px-3 w-full"
                  style={{ minHeight: "36px", borderColor: "#e5e7eb" }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <label className="text-xs font-medium text-muted-foreground" style={{ color: "#6b7280" }}>Product Number</label>
                <input
                  value={productNumber}
                  onChange={(e) => setProductNumber(e.target.value)}
                  placeholder="e.g. GDL-2025-001"
                  className="h-9 text-sm rounded-md border border-input px-3 w-full"
                  style={{ minHeight: "36px", borderColor: "#e5e7eb" }}
                />
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label className="text-xs font-medium text-muted-foreground" style={{ color: "#6b7280" }}>Variant / Description</label>
              <input
                value={variant}
                onChange={(e) => setVariant(e.target.value)}
                placeholder="e.g. 1.23×1.48 m – Triple glazed 65G"
                className="h-9 text-sm rounded-md border border-input px-3 w-full"
                style={{ minHeight: "36px", borderColor: "#e5e7eb" }}
              />
            </div>

            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pt-1" style={{ color: "#6b7280", fontSize: "11px", paddingTop: "4px" }}>
              Functional Unit (FU)
            </h4>
            <div className="flex items-center gap-2" style={{ display: "flex", gap: "8px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1 }}>
                <label className="text-xs font-medium text-muted-foreground" style={{ color: "#6b7280" }}>Quantity</label>
                <input
                  type="number"
                  min="0"
                  value={fuValue}
                  onChange={(e) => setFuValue(e.target.value)}
                  className="h-9 text-sm rounded-md border border-input px-3 w-full"
                  style={{ minHeight: "36px", borderColor: "#e5e7eb" }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1 }}>
                <label className="text-xs font-medium text-muted-foreground" style={{ color: "#6b7280" }}>Unit</label>
                <select
                  value={fuUnit}
                  onChange={(e) => setFuUnit(e.target.value)}
                  className="h-9 text-sm rounded-md border border-input px-3 w-full"
                  style={{ minHeight: "36px", borderColor: "#e5e7eb" }}
                >
                  {FU_UNITS.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label className="text-xs font-medium text-muted-foreground" style={{ color: "#6b7280" }}>Category</label>
              <select
                value={categoryId}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="h-9 text-sm rounded-md border border-input px-3 w-full"
                style={{ minHeight: "36px", borderColor: "#e5e7eb" }}
              >
                {categoriesList.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
          </section>

          {/* Product Variation */}
          <section style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div className="flex items-center justify-between" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider" style={{ color: "#6b7280", fontSize: "11px" }}>
                Product Variation
              </h4>
              <span className="text-[10px] text-muted-foreground" style={{ color: "#6b7280" }}>Shown in the blue header bar on the product page</span>
            </div>

            <div className="rounded-xl border border-border overflow-hidden" style={{ borderRadius: "12px", border: "1px solid #e5e7eb" }}>
              {params.map((param, i) => (
                <div
                  key={param.key}
                  className={i > 0 ? "border-t border-border" : ""}
                  style={{
                    borderTop: i > 0 ? "1px solid #e5e7eb" : "none",
                    padding: "10px 16px",
                  }}
                >
                  <div
                    className="flex items-center gap-3"
                    style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}
                  >
                    <span
                      className="text-xs font-medium text-foreground w-28 shrink-0"
                      style={{ width: "112px", flexShrink: 0 }}
                    >
                      {param.label}
                    </span>
                    {(param.values || []).length > 0 && (
                      <span style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                        {(param.values || []).map((v) => (
                          <span
                            key={v}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "2px 8px",
                              borderRadius: "999px",
                              border: "1px solid rgba(59,130,246,0.4)",
                              background: "rgba(59,130,246,0.12)",
                              color: "hsl(221 39% 24%)",
                              fontSize: "11px",
                            }}
                          >
                            {v}
                            <button
                              type="button"
                              onClick={() => removeParamValue(param.key, v)}
                              style={{ padding: 0, border: "none", background: "none", cursor: "pointer", display: "flex" }}
                              aria-label={`Remove ${v}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </span>
                    )}
                    <input
                      placeholder="Add value..."
                      value={draftValueByKey[param.key] || ""}
                      onChange={(e) => setDraftValueByKey((prev) => ({ ...prev, [param.key]: e.target.value }))}
                      className="h-8 text-xs flex-1 min-w-[100px] border border-input rounded px-2"
                      style={{
                        height: "32px",
                        fontSize: "12px",
                        flex: "1 1 100px",
                        background: "rgba(0,0,0,0.04)",
                        borderColor: "#e5e7eb",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const v = (draftValueByKey[param.key] || "").trim();
                          if (v) addParamValue(param.key, v);
                          setDraftValueByKey((prev) => ({ ...prev, [param.key]: "" }));
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const v = (draftValueByKey[param.key] || "").trim();
                        if (v) addParamValue(param.key, v);
                        setDraftValueByKey((prev) => ({ ...prev, [param.key]: "" }));
                      }}
                      style={{
                        padding: "4px 8px",
                        fontSize: "11px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        cursor: "pointer",
                        background: "rgba(0,0,0,0.04)",
                      }}
                    >
                      <Plus className="h-3 w-3 inline" /> Add
                    </button>
                    <button
                      type="button"
                      onClick={() => removeParam(param.key)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                      style={{ color: "#6b7280" }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {param.options && param.options.length > 0 && (
                    <div
                      style={{
                        marginTop: "8px",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        fontSize: "10px",
                        color: "#6b7280",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ opacity: 0.9, marginRight: "4px" }}>Existing (click to toggle):</span>
                      {param.options.map((opt) => {
                        const selected = (param.values || []).includes(opt);
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => toggleParamValue(param.key, opt)}
                            style={{
                              padding: "2px 8px",
                              borderRadius: "999px",
                              border: "1px solid #e5e7eb",
                              background: selected ? "rgba(59,130,246,0.12)" : "rgba(243,244,246,0.8)",
                              color: selected ? "hsl(221 39% 24%)" : "#4b5563",
                              cursor: "pointer",
                            }}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2" style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <select
                value=""
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) addParam(val);
                  e.target.value = "";
                }}
                className="h-8 text-xs flex-1 rounded-md border border-input px-3 min-w-0"
                style={{ height: "32px", fontSize: "12px", flex: "1 1 120px", minWidth: "120px", borderColor: "#e5e7eb" }}
              >
                <option value="">Add variable…</option>
                {knownVariables
                  .filter((lbl) => !params.some((p) => p.key === lbl.toLowerCase().replace(/\s+/g, "-")))
                  .map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
              </select>
              <input
                value={newParamLabel}
                onChange={(e) => setNewParamLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addParam(newParamLabel);
                  }
                }}
                placeholder="Or type a new variable…"
                className="h-8 text-xs flex-1 rounded-md border border-input px-3 min-w-0"
                style={{ height: "32px", fontSize: "12px", flex: "1 1 140px", minWidth: "140px", borderColor: "#e5e7eb" }}
              />
              <button
                type="button"
                onClick={() => addParam(newParamLabel)}
                className="h-8 text-xs px-3 shrink-0 rounded-md border border-border"
                style={{ height: "32px", fontSize: "12px", padding: "0 12px", border: "1px solid #e5e7eb" }}
              >
                <Plus className="h-3 w-3 mr-1 inline" /> Add
              </button>
            </div>
          </section>
        </div>

        <div
          className="flex justify-end gap-2 px-6 py-4 border-t border-border sticky bottom-0 bg-card"
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px", padding: "16px 24px", borderTop: "1px solid #e5e7eb" }}
        >
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-1.5 text-sm"
            style={{ border: "1px solid #e5e7eb", padding: "6px 12px" }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!name.trim()}
            onClick={handleConfirm}
            className="rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm font-medium disabled:opacity-50"
            style={{
              background: "var(--catalog-navy-bg, rgba(33, 55, 97, 0.85))",
              color: "#fff",
              padding: "6px 12px",
              opacity: name.trim() ? 1 : 0.5,
            }}
          >
            Create Product
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewProductDialog;
